import React from 'react';
import PropTypes from 'prop-types';
import Divider from 'components/divider';
import LinearAxisTickLabel from 'components/linear-axis-tick-label';
import SvgText from 'components/svg-text';
import * as Orient from 'constants/orient';
import * as ValueType from 'constants/value-type';
import {getTranslate} from 'utils/attribute-util';
import {getTickY} from 'utils/layout-util';
import {getTicks} from 'utils/tick-util';
import Colors from 'common/constants/colors';
import styles from './index.css';

export default class LinearAxis extends React.Component {
  static propTypes = {
    divider: PropTypes.bool,
    height: PropTypes.number,
    label: PropTypes.string,
    numTicks: PropTypes.number,
    orient: PropTypes.oneOf([Orient.LEFT, Orient.RIGHT, Orient.TOP]),
    scale: PropTypes.func,
    showDividerAtZero: PropTypes.bool,
    style: PropTypes.object,
    tickPadding: PropTypes.number,
    ticks: PropTypes.array,
    valueType: PropTypes.oneOf(Object.values(ValueType)),
    width: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
    zeroY: PropTypes.number,
  };

  static defaultProps = {
    divider: false,
    height: 0,
    label: '',
    numTicks: 0,
    orient: Orient.LEFT,
    scale: () => {},
    showDividerAtZero: true,
    style: {},
    tickPadding: 0,
    ticks: [],
    valueType: ValueType.NUMERIC,
    width: 0,
    x: 0,
    y: 0,
    zeroY: 0,
  };

  getDivider(orient, width, height) {
    switch (orient) {
      case Orient.TOP:
        return {
          x1: 0,
          x2: width,
          y1: height,
          y2: height,
        };
      default:
        return {
          x1: 0,
          x2: 0,
          y1: 0,
          y2: height,
        };
    }
  }

  getLabelTransform(orient, height) {
    const degrees = orient === Orient.LEFT ? 270 : 90;
    return `rotate(${degrees} 0 ${height / 2})`;
  }

  getTickColor(fill) {
    return fill || null;
  }

  getTickPositions(orient, ticks, tickX, tickPadding, scale, width, height) {
    switch (orient) {
      case Orient.LEFT:
      case Orient.RIGHT:
        return ticks.map((datum) => {
          return {
            x: tickX,
            y: getTickY(scale, datum),
          };
        });
      default:
        return ticks.map((datum) => {
          return {
            x: scale(datum),
            y: tickPadding !== 0 ? height - tickPadding : height,
          };
        });
    }
  }

  getTickStyles(orient, ticks, fill) {
    switch (orient) {
      case Orient.LEFT:
      case Orient.RIGHT:
        return ticks.map(() => ({
          fill,
        }));
      default:
        return ticks.map((datum, index) => ({
          fill,
          textAnchor: index > 0 ? 'middle' : 'start',
        }));
    }
  }

  getTickX(orient, tickPadding) {
    switch (orient) {
      case Orient.LEFT:
        return -tickPadding;
      case Orient.RIGHT:
        return tickPadding;
      default:
        return 0;
    }
  }

  render() {
    const divider = this.getDivider(
      this.props.orient,
      this.props.width,
      this.props.height,
    );
    const ticks = getTicks(this.props.scale, this.props.numTicks);
    const tickX = this.getTickX(this.props.orient, this.props.tickPadding);
    const tickPositions = this.getTickPositions(
      this.props.orient,
      ticks,
      tickX,
      this.props.tickPadding,
      this.props.scale,
      this.props.width,
      this.props.height,
    );
    const tickColor = this.getTickColor(this.props.style.fill, Colors.gray400);
    const tickStyles = this.getTickStyles(this.props.orient, ticks, tickColor);
    const translate = getTranslate(this.props.x, this.props.y);
    const labelTransform = this.getLabelTransform(
      this.props.orient,
      this.props.height,
    );
    return (
      <g className={styles.LinearAxis} transform={translate}>
        {this.props.divider ? (
          <Divider
            style={{stroke: tickColor}}
            x1={divider.x1}
            x2={divider.x2}
            y1={divider.y1}
            y2={divider.y2}
          />
        ) : null}

        {ticks.map((datum, index) => (
          <LinearAxisTickLabel
            index={index}
            key={index}
            numTicks={ticks.length}
            orient={this.props.orient}
            style={tickStyles[index]}
            value={datum}
            valueType={this.props.valueType}
            x={tickPositions[index].x}
            y={tickPositions[index].y}
          />
        ))}

        {this.props.divider && this.props.showDividerAtZero ? (
          <Divider
            style={{stroke: this.props.style.fill}}
            x1={0}
            x2={this.props.width}
            y1={this.props.zeroY}
            y2={this.props.zeroY}
          />
        ) : null}

        {this.props.label ? (
          <SvgText
            style={{
              dominantBaseline: 'text-before-edge',
              fill: tickColor,
              textAnchor: 'middle',
            }}
            text={this.props.label}
            transform={labelTransform}
            y={this.props.height / 2 + this.props.tickPadding}
          />
        ) : null}
      </g>
    );
  }
}
